import { HydratedRouter } from 'react-router/dom';
import { startTransition, StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import Fetch from 'i18next-fetch-backend';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import i18next from 'i18next';
import I18nextBrowserLanguageDetector from 'i18next-browser-languagedetector';
import { initSentryTracking } from '~/tracking/sentry';
import { fallbackLng, supportedLngs } from '../app/i18nConfig';
import { logToConsole } from '~/logging/consoleLogger';
import { installTranslationCrashShim } from '~/common/utils/installTranslationCrashShim.client';

async function main() {
  installTranslationCrashShim();

  logToConsole({ loggerName: 'entry-client', logLevel: 'INFO', message: 'CLIENT DEPLOYMENT CONFIG', args: [window.TG_CLIENT_CONFIG] });

  initSentryTracking();

  await i18next
    .use(initReactI18next) // Tell i18next to use the react-i18next plugin
    .use(Fetch) // Tell i18next to use the Fetch backend
    .use(I18nextBrowserLanguageDetector) // Setup a client-side language detector
    .init({
      fallbackLng,
      // Reject any detected language outside this set and fall back. Browser
      // translation extensions rewrite `<html lang>` (e.g. to "en") before our
      // detector runs, which would otherwise cause URLs built from
      // `i18n.language` to point at non-existent routes like /en/resources/cart.
      supportedLngs,
      nonExplicitSupportedLngs: false,
      detection: {
        // Here only enable htmlTag detection, we'll detect the language only
        // server-side with remix-i18next, by using the `<html lang>` attribute
        // we can communicate to the client the language detected server-side
        order: ['htmlTag'],
        // Because we only use htmlTag, there's no reason to cache the language
        // on the browser, so we disable it
        caches: []
      },
      backend: {
        // We will configure the backend to fetch the translations from the
        // resource route /infra/locales and pass the lng and ns as search params
        loadPath: '/infra/locales/{{lng}}/{{ns}}'
      }
    });

  startTransition(() => {
    hydrateRoot(
      document,
      <I18nextProvider i18n={i18next}>
        <StrictMode>
          <HydratedRouter />
        </StrictMode>
      </I18nextProvider>
    );
  });
}

try {
  await main();
} catch (error) {
  logToConsole({
    loggerName: 'entry-client',
    logLevel: 'ERROR',
    message: 'Error during client initialization',
    args: [error]
  });
}
